import { useAuth } from '@cibo/profile'
import { Alert, AlertTitle, CircularProgress, useTheme } from '@mui/material'
import ROUTE from 'navigation/types/routes'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { generatePath } from 'react-router-dom'
import { usePaginatedControlGroups } from '..'
import { useAgreement } from '../../Agreements'

export const ControlPlotsAlert = () => {
  const { t } = useTranslation('impact/components/ControlPlotsAlert')
  const navigate = useNavigate()

  // @TODO Get these from an "action items" type endpoint like CPD-3177
  const controlGroups = usePaginatedControlGroups()
  const uncommittedControlGroups = controlGroups.data?.items.filter(
    controlGroup => !controlGroup.isCommitted
  )

  const uncommittedAgreements =
    uncommittedControlGroups
      ?.map(controlGroup => controlGroup.agreementId)
      .reduce<Record<string, number>>((acc, agreementId) => {
        if (!!agreementId) {
          if (!acc[agreementId]) {
            acc[agreementId] = 1
          } else {
            acc[agreementId]++
          }
        }
        return acc
      }, {}) ?? {}

  const {
    palette: { mode },
  } = useTheme()

  // @TODO: Provide some identifying info about program/enrollment related to
  // these control plots. Agreements are not related to a program currently.
  // See CPD-3177
  return !!uncommittedAgreements && Object.keys(uncommittedAgreements).length > 0 ? (
    Object.keys(uncommittedAgreements).length === 1 ? (
      <SingleControlPlotsAlert
        agreementId={Object.keys(uncommittedAgreements)[0]}
        count={Object.values(uncommittedAgreements)[0]}
      />
    ) : (
      <Alert
        onClick={() => navigate(generatePath(ROUTE.CONTROL_GROUPS))}
        sx={{ '&:hover': { cursor: 'pointer' } }}
        severity="warning"
        icon={controlGroups.isLoading ? <CircularProgress size={22} /> : undefined}
        variant={mode === 'dark' ? 'filled' : 'standard'}
        data-testid="control-groups-alert"
      >
        <AlertTitle>
          {t('multipleControlPlotsAlert', { count: Object.keys(uncommittedAgreements).length })}
        </AlertTitle>

        <Trans
          i18nKey={`impact/components/ControlPlotsAlert:controlGroupsDescription`}
          count={Object.keys(uncommittedAgreements).length}
        >
          Some control groups are unconfirmed. <strong>click here</strong> to view all control
          groups
        </Trans>
      </Alert>
    )
  ) : null
}

const SingleControlPlotsAlert = ({
  agreementId,
  count,
}: {
  agreementId: string
  count: number
}) => {
  const { t } = useTranslation('impact/components/ControlPlotsAlert')
  const agreement = useAgreement(agreementId)
  const navigate = useNavigate()
  const { persona } = useAuth()
  const {
    palette: { mode },
  } = useTheme()

  if (!agreement.data) return null

  return (
    <Alert
      onClick={() => navigate(generatePath(ROUTE.CONTROL_GROUPS_DETAIL, { agreementId }))}
      sx={{ '&:hover': { cursor: 'pointer' } }}
      severity="warning"
      variant={mode === 'dark' ? 'filled' : 'standard'}
      key={agreementId}
      data-testid="control-plots-alert"
    >
      <AlertTitle>
        {t('singleControlPlotsAlert', {
          context: persona,
          count,
          name: agreement.data.userModel.displayNameFull,
        })}
      </AlertTitle>

      <Trans i18nKey={`impact/components/ControlPlotsAlert:descriptionSingle`} count={count}>
        Control plots determine sampling plan <strong>click here to set control plots</strong>
      </Trans>
    </Alert>
  )
}
